<template>
  <div class="card" v-bind="$attrs">
    <div class="card-header d-flex justify-content-between">
      <h5 class="text-uppercase mb-0">Chambres</h5>
      <button class="btn btn-link link-dark p-0 link-underline-opacity-0 link-underline-opacity-100-hover" @click="addRoom()"><i class="fa-solid fa-plus pe-1"></i>Ajouter une chambre</button>
    </div>
    <div class="card-body">
      <div v-if="rooms.length === 0" class="text-muted">
        Aucune chambre ajoutée.
      </div>
      <div v-else v-for="(room, idx) in rooms" :key="room.key" class="mb-2">
        <span :id="`${props.valueKey}[${idx}]`" class="anchor"></span>
        <div class="row">
          <div class="col-12 col-md-6">
            <TextInputComponent
              :value-key="`${props.valueKey}[${idx}].name`"
              label="Nom"
              placeholder="Nom"
              :required="true"
              class="mb-3"
            />
          </div>
          <div class="col-12 col-md-2">
            <SelectInputComponent
              :value-key="`${props.valueKey}[${idx}].type`"
              label="Type de chambre"
              placeholder="Sélectionnez un type de chambre"
              :options="Object.values(TravelDbHotelRoomTypeEnum).map(type => ({ value: type, label: GetHotelRoomTypeLabelLong(type) }))"
              :required="true"
              :close-on-select="false"
              class="mb-3 mb-md-3"
            />
          </div>
          <div class="col-12 col-md-3">
            <NumberInputComponent
              :value-key="`${props.valueKey}[${idx}].price`"
              label="Prix"
              placeholder="Prix"
              :required="true"
              type="euro"
              group-text="/ nuit / pers"
              class="mb-3 mb-md-0"
            />
          </div>
          <div class="col-12 col-md-1 d-flex align-items-center justify-content-end order-first order-md-last">
            <i role="button" class="fa-regular fa-trash-can" @click="removeRoom(idx)"></i>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-md-11">
            <FileUploadComponent
                type="image"
                :value-key="`${props.valueKey}[${idx}].picture`"
                :required="false"
                class="mb-3"
                height="75px"
                :hide-icon="true"
                :display="'contains'"
                :display-max-height="150"
              />
          </div>
          <hr class="mt-2" v-if="idx < rooms.length - 1" />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted } from 'vue';
import { useFieldArray } from 'vee-validate';
import { IdNotDefined, GetHotelRoomTypeLabelLong } from '@/compositions/helper'
import TextInputComponent from '@/components/common/TextInputComponent.vue';
import * as zod from 'zod';

const props = defineProps<{
  valueKey: AllTypeKeys;
}>();

const validationSchema = defineModel<{ shape: zod.ZodRawShape, anchors?: Record<string, string  | undefined> }>('validation-schema',{ required: false, default: () => ({ shape: {} }) });

const { remove: removeRoom, push: pushRoom, fields: rooms } = useFieldArray<TravelDbHotelRoom>(() => props.valueKey);

const addRoom = () => {
  pushRoom(<TravelDbHotelRoom>{
    id: IdNotDefined,
    name: '',
    type: TravelDbHotelRoomTypeEnum.Double,
    price: 0,
  });
}

onMounted(() => {
  validationSchema.value = {
    shape: {
      rooms: zod.array(zod.object({
        id: zod.number().nonnegative(),
        name: zod.string().min(1, 'Le nom de la chambre est obligatoire'),
        type: zod.nativeEnum(TravelDbHotelRoomTypeEnum, { message: 'Le type de la chambre est obligatoire' }),
        price: zod.number({ message:'Le prix de la chambre est obligatoire'}),
        picture: zod.object({
          id: zod.number().nonnegative(),
          key: zod.string().uuid().optional(),
          mimeType: zod.string().optional(),
          base64Content: zod.string().optional()
         }).optional()
      })).optional()
  },
  anchors: {
    [`${props.valueKey}[\\d].id`]: "self",
    [`${props.valueKey}[\\d].name`]: "self",
    [`${props.valueKey}[\\d].type`]: "self",
    [`${props.valueKey}[\\d].price`]: "self",
    [`${props.valueKey}[\\d].picture`]: "self",
  }
}
});
</script>
